import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth, storage } from '../firebaseConfig';
import { collection, query, where, onSnapshot, getDoc, doc, addDoc, getDocs, orderBy, serverTimestamp, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { IoArrowBack, IoWarningOutline, IoAddCircleOutline, IoSearchOutline, IoCloseOutline, IoPeopleOutline, IoTrashOutline, IoDocumentTextOutline, IoCameraOutline, IoVideocamOutline, IoImageOutline, IoRadioButtonOn, IoCheckmarkCircle, IoAlertCircleOutline } from 'react-icons/io5';
import moment from 'moment';
import './ApercibimientosScreen.css';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import { useAuth } from '../context/AuthContext';
import AIAssistantButton from '../components/Shared/AIAssistantButton';

function ApercibimientosScreen() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState('');
    const [condoId, setCondoId] = useState('');
    const [condoConfig, setCondoConfig] = useState(null);

    // List of sanctions
    const [records, setRecords] = useState([]);
    const [filteredRecords, setFilteredRecords] = useState([]);

    // Filter states
    const [filterTipo, setFilterTipo] = useState('todos'); // todos | apercibimiento | multa
    const [filterSearch, setFilterSearch] = useState('');

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);

    // Toast Notification State
    const [toast, setToast] = useState(null); // { text, type: 'success' | 'error' }
    const showToastMsg = (text, type = 'success') => {
        setToast({ text, type });
        setTimeout(() => setToast(null), 4000);
    };

    // Form state
    const [selectedResidenteId, setSelectedResidenteId] = useState('');
    const [selectedCategoria, setSelectedCategoria] = useState('');
    const [sanctionTipo, setSanctionTipo] = useState('apercibimiento'); // apercibimiento | multa
    const [descripcion, setDescripcion] = useState('');
    const [evidenciaUrl, setEvidenciaUrl] = useState('');

    // Available residents list for dropdown
    const [residentesList, setResidentesList] = useState([]);

    // Evidence State
    const [evidenceFile, setEvidenceFile] = useState(null);
    const [evidencePreview, setEvidencePreview] = useState('');
    const [evidenceType, setEvidenceType] = useState(''); // 'image' | 'video'

    // Live Camera/Microphone State
    const [showCamera, setShowCamera] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [cameraMode, setCameraMode] = useState('photo'); // 'photo' | 'video'
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [recordedChunks, setRecordedChunks] = useState([]);

    // Camera control methods
    const startCamera = async (mode) => {
        try {
            setCameraMode(mode);
            const constraints = {
                video: { facingMode: 'environment' },
                audio: mode === 'video'
            };
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            setCameraStream(stream);
            setShowCamera(true);

            // Set stream to video element
            setTimeout(() => {
                const videoEl = document.getElementById('camera-preview-video');
                if (videoEl) videoEl.srcObject = stream;
            }, 300);
        } catch (e) {
            console.error(e);
            alert('No se pudo acceder a la cámara/micrófono: ' + e.message);
        }
    };

    const stopCamera = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => track.stop());
            setCameraStream(null);
        }
        setShowCamera(false);
        setIsRecording(false);
        setMediaRecorder(null);
        setRecordedChunks([]);
    };

    const capturePhoto = () => {
        const videoEl = document.getElementById('camera-preview-video');
        if (!videoEl) return;

        const canvas = document.createElement('canvas');
        canvas.width = videoEl.videoWidth || 640;
        canvas.height = videoEl.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            const file = new File([blob], `evidencia_${Date.now()}.jpg`, { type: 'image/jpeg' });
            setEvidenceFile(file);
            setEvidenceType('image');
            setEvidencePreview(URL.createObjectURL(file));
            stopCamera();
        }, 'image/jpeg', 0.95);
    };

    const startRecordingVideo = () => {
        if (!cameraStream) return;
        const recorder = new MediaRecorder(cameraStream, { mimeType: 'video/webm' });
        const chunks = [];
        recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) chunks.push(e.data);
        };
        recorder.onstop = () => {
            const blob = new Blob(chunks, { type: 'video/webm' });
            const file = new File([blob], `evidencia_${Date.now()}.webm`, { type: 'video/webm' });
            setEvidenceFile(file);
            setEvidenceType('video');
            setEvidencePreview(URL.createObjectURL(file));
        };
        setRecordedChunks(chunks);
        setMediaRecorder(recorder);
        recorder.start();
        setIsRecording(true);
    };

    const stopRecordingVideo = () => {
        if (mediaRecorder && isRecording) {
            mediaRecorder.stop();
            setIsRecording(false);
            stopCamera();
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEvidenceFile(file);
            const type = file.type.startsWith('video/') ? 'video' : 'image';
            setEvidenceType(type);
            setEvidencePreview(URL.createObjectURL(file));
        }
    };

    useEffect(() => {
        if (!currentUser) return;
        loadUserInfo();
    }, [currentUser]);

    const loadUserInfo = async () => {
        try {
            const userRef = doc(db, 'usuarios', currentUser.uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
                const uData = userSnap.data();
                setRole(uData.rol);
                setCondoId(uData.condominioId);
                loadCondoConfig(uData.condominioId);
                subscribeRecords(uData.condominioId, uData.rol, uData.numeroCasa);
                loadResidentes(uData.condominioId);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const loadCondoConfig = async (condoIdVal) => {
        try {
            const condoSnap = await getDoc(doc(db, 'condominios', condoIdVal));
            if (condoSnap.exists()) {
                setCondoConfig(condoSnap.data());
            }
        } catch (e) {
            console.error(e);
        }
    };

    const loadResidentes = async (condoIdVal) => {
        try {
            const q = query(
                collection(db, 'usuarios'),
                where('condominioId', '==', condoIdVal),
                where('rol', '==', 'residente')
            );
            const snap = await getDocs(q);
            setResidentesList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error(e);
        }
    };

    const subscribeRecords = (condoIdVal, userRole, houseNum) => {
        let q;
        if (userRole === 'residente') {
            if (houseNum && houseNum !== 'N/A') {
                q = query(
                    collection(db, 'apercibimientos_multas'),
                    where('condominioId', '==', condoIdVal),
                    where('numeroCasa', '==', houseNum),
                    orderBy('fechaCreacion', 'desc')
                );
            } else {
                q = query(
                    collection(db, 'apercibimientos_multas'),
                    where('condominioId', '==', condoIdVal),
                    where('residenteId', '==', currentUser.uid),
                    orderBy('fechaCreacion', 'desc')
                );
            }
        } else {
            q = query(
                collection(db, 'apercibimientos_multas'),
                where('condominioId', '==', condoIdVal),
                orderBy('fechaCreacion', 'desc')
            );
        }

        const unsub = onSnapshot(q, (snap) => {
            const list = snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                fechaCreacion: d.data().fechaCreacion?.toDate() || new Date()
            }));
            setRecords(list);
            setFilteredRecords(list);
            setLoading(false);

            if (userRole === 'residente') {
                const unreadDocs = snap.docs.filter(d => d.data().leido === false);
                if (unreadDocs.length > 0) {
                    unreadDocs.forEach(async (d) => {
                        try {
                            await updateDoc(doc(db, 'apercibimientos_multas', d.id), { leido: true });
                        } catch (e) {
                            console.error("Error updating read status for sanction:", e);
                        }
                    });
                }
            }
        }, (err) => {
            console.error(err);
            setLoading(false);
        });

        return () => unsub();
    };

    useEffect(() => {
        let list = [...records];

        if (filterTipo !== 'todos') {
            list = list.filter(r => r.tipo === filterTipo);
        }

        if (filterSearch.trim() !== '') {
            const s = filterSearch.toLowerCase();
            list = list.filter(r =>
                (r.residenteNombre && r.residenteNombre.toLowerCase().includes(s)) ||
                (r.numeroCasa && r.numeroCasa.toLowerCase().includes(s)) ||
                (r.categoria && r.categoria.toLowerCase().includes(s)) ||
                (r.descripcion && r.descripcion.toLowerCase().includes(s))
            );
        }

        setFilteredRecords(list);
    }, [filterTipo, filterSearch, records]);

    const handleCreateSanction = async (e) => {
        e.preventDefault();
        if (!selectedResidenteId || !selectedCategoria || !descripcion) {
            showToastMsg('Por favor completa los campos requeridos.', 'error');
            return;
        }

        setModalLoading(true);
        try {
            const resData = residentesList.find(r => r.id === selectedResidenteId);
            if (!resData) throw new Error('Residente no encontrado');

            const condoRules = condoConfig?.warningsConfig || {
                threshold: 3,
                timeframeDays: 30,
                autoFineAmount: 500
            };

            // Upload evidence file if exists
            let finalEvidenciaUrl = evidenciaUrl;
            if (evidenceFile) {
                const storageRef = ref(storage, `sanciones/${condoId}/${Date.now()}_${evidenceFile.name}`);
                const snapshot = await uploadBytes(storageRef, evidenceFile);
                finalEvidenciaUrl = await getDownloadURL(snapshot.ref);
            }

            // Save warning / fine doc
            const sanctionDoc = {
                condominioId: condoId,
                residenteId: selectedResidenteId,
                residenteNombre: resData.nombre,
                numeroCasa: resData.numeroCasa || 'N/A',
                tipo: sanctionTipo,
                categoria: selectedCategoria,
                descripcion,
                evidenciaUrl: finalEvidenciaUrl || '',
                fechaCreacion: serverTimestamp(),
                leido: false
            };

            const docRef = await addDoc(collection(db, 'apercibimientos_multas'), sanctionDoc);

            // Send notification
            await addDoc(collection(db, 'notificaciones'), {
                residenteId: selectedResidenteId,
                condominioId: condoId,
                titulo: sanctionTipo === 'apercibimiento' ? 'Nuevo Apercibimiento Registrado' : 'Multa Aplicada',
                mensaje: `Se ha registrado una sanción de tipo ${sanctionTipo} por la categoría "${selectedCategoria}": ${descripcion}`,
                leido: false,
                fechaCreacion: serverTimestamp()
            });

            // If it is warning ('apercibimiento'), check if limit is reached for auto-fine
            if (sanctionTipo === 'apercibimiento') {
                const limitTime = new Date();
                limitTime.setDate(limitTime.getDate() - (condoRules.timeframeDays || 30));

                const houseNum = resData.numeroCasa || 'N/A';
                let warningsQuery;
                if (houseNum && houseNum !== 'N/A') {
                    warningsQuery = query(
                        collection(db, 'apercibimientos_multas'),
                        where('condominioId', '==', condoId),
                        where('numeroCasa', '==', houseNum),
                        where('tipo', '==', 'apercibimiento')
                    );
                } else {
                    warningsQuery = query(
                        collection(db, 'apercibimientos_multas'),
                        where('condominioId', '==', condoId),
                        where('residenteId', '==', selectedResidenteId),
                        where('tipo', '==', 'apercibimiento')
                    );
                }
                const warningsSnap = await getDocs(warningsQuery);
                const warningDocs = warningsSnap.docs.map(d => ({
                    ...d.data(),
                    fechaCreacion: d.data().fechaCreacion?.toDate() || new Date()
                }));

                const recentWarnings = warningDocs.filter(w => w.fechaCreacion >= limitTime && w.categoria === selectedCategoria);

                // Check if threshold is reached
                if (recentWarnings.length >= (condoRules.threshold || 3)) {
                    // Create automatic fine cargo
                    const cargoRef = await addDoc(collection(db, 'cargos'), {
                        condominioId: condoId,
                        residenteId: selectedResidenteId,
                        unidadResidente: resData.numeroCasa || 'N/A',
                        concepto: `Multa Automática por acumulación de Apercibimientos (${selectedCategoria})`,
                        monto: condoRules.autoFineAmount || 500,
                        montoPagado: 0,
                        estatus: 'pendiente',
                        fechaVencimiento: new Date(),
                        fechaCreacion: new Date(),
                        tipo: 'Multa'
                    });

                    // Create fine record in warnings/fines list
                    await addDoc(collection(db, 'apercibimientos_multas'), {
                        condominioId: condoId,
                        residenteId: selectedResidenteId,
                        residenteNombre: resData.nombre,
                        numeroCasa: resData.numeroCasa || 'N/A',
                        tipo: 'multa',
                        categoria: selectedCategoria,
                        descripcion: `Generada automáticamente al acumular ${recentWarnings.length} apercibimientos en ${condoRules.timeframeDays} días.`,
                        evidenciaUrl: '',
                        fechaCreacion: serverTimestamp(),
                        cargoId: cargoRef.id,
                        leido: false
                    });

                    // Send notification for automated fine
                    await addDoc(collection(db, 'notificaciones'), {
                        residenteId: selectedResidenteId,
                        condominioId: condoId,
                        titulo: 'Multa Generada Automáticamente',
                        mensaje: `Se ha generado una multa de $${condoRules.autoFineAmount} MXN por acumulación de apercibimientos.`,
                        leido: false,
                        fechaCreacion: serverTimestamp()
                    });
                }
            }

            showToastMsg('Sanción registrada correctamente.', 'success');
            setShowModal(false);
            // reset form
            setSelectedResidenteId('');
            setSelectedCategoria('');
            setSanctionTipo('apercibimiento');
            setDescripcion('');
            setEvidenciaUrl('');
            setEvidenceFile(null);
            setEvidencePreview('');
            setEvidenceType('');
        } catch (e) {
            console.error(e);
            showToastMsg('Error al registrar sanción: ' + e.message, 'error');
        } finally {
            setModalLoading(false);
        }
    };

    const categoriesList = condoConfig?.warningsConfig?.categories || [
        'Ruido', 'Cochera Obstruida', 'Pasto Largo', 'Basura', 'Mascotas'
    ];

    return (
        <div className="ks-page">
            {toast && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: toast.type === 'success' ? '#ecfdf5' : '#fee2e2',
                    color: toast.type === 'success' ? '#047857' : '#b91c1c',
                    border: `1px solid ${toast.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
                    padding: '12px 24px',
                    borderRadius: '30px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: '600',
                    fontSize: '0.95rem',
                    animation: 'slideDown 0.3s ease-out'
                }}>
                    {toast.type === 'success' ? <IoCheckmarkCircle size={20} /> : <IoAlertCircleOutline size={20} />}
                    {toast.text}
                </div>
            )}
            <style>{`
                @keyframes slideDown {
                    from { transform: translate(-50%, -20px); opacity: 0; }
                    to { transform: translate(-50%, 0); opacity: 1; }
                }
            `}</style>
            <KondoSidebar activeRoute="/apercibimientos" />
            <main className="ks-main">
                <div className="ks-header-sticky-wrapper">
                    <KondoBrandHeader title="Administración" showBackButton={false} />
                </div>

                <div className="ks-scroll-area">
                    {/* --- Premium Hero Banner --- */}
                    <div className="muro-header-stitch">
                        <div className="muro-hero-card">
                            <div className="muro-hero-content">
                                <h1>Sanciones y Advertencias</h1>
                                <p>Historial de apercibimientos y multas</p>
                                
                                {['admin', 'comite'].includes(role) && (
                                    <button
                                        onClick={() => setShowModal(true)}
                                        className="hero-publish-btn"
                                    >
                                        <IoAddCircleOutline size={20} />
                                        <span>Sanción</span>
                                    </button>
                                )}
                            </div>

                            <IoWarningOutline className="muro-hero-icon" />
                        </div>
                    </div>

                    <div className="apercibimientos-dashboard-body">

                        {/* Filters and Actions Bar */}
                        <div className="actions-bar-container">
                            <div className="search-box">
                                <IoSearchOutline size={18} />
                                <input
                                    type="text"
                                    placeholder="Buscar por casa, nombre, descripción..."
                                    value={filterSearch}
                                    onChange={(e) => setFilterSearch(e.target.value)}
                                />
                            </div>

                            <div className="filters-right">
                                <select
                                    className="filter-select"
                                    value={filterTipo}
                                    onChange={(e) => setFilterTipo(e.target.value)}
                                >
                                    <option value="todos">Todos los registros</option>
                                    <option value="apercibimiento">Apercibimientos</option>
                                    <option value="multa">Multas</option>
                                </select>
                            </div>
                        </div>

                        {/* Summary panel of rules */}
                        {condoConfig?.warningsConfig && (
                            <div className="rules-summary-bar">
                                <span>💡 Regla Vigente: <strong>{condoConfig.warningsConfig.threshold} apercibimientos</strong> en <strong>{condoConfig.warningsConfig.timeframeDays} días</strong> generan una multa automática de <strong>${condoConfig.warningsConfig.autoFineAmount} MXN</strong>.</span>
                            </div>
                        )}

                        {loading ? (
                            <div className="loading-state">Cargando registros de sanciones...</div>
                        ) : filteredRecords.length === 0 ? (
                            <div className="empty-state">
                                <IoWarningOutline size={60} style={{ color: '#cbd5e1', marginBottom: 15 }} />
                                <h3>Sin registros</h3>
                                <p>No se encontraron sanciones ni apercibimientos con los filtros seleccionados.</p>
                            </div>
                        ) : (
                            <div className="records-list">
                                {filteredRecords.map((item) => (
                                    <div key={item.id} className={`record-card ${item.tipo}`}>
                                        <div className="record-card-header">
                                            <span className={`badge-tipo ${item.tipo}`}>{item.tipo}</span>
                                            <span className="record-date">{moment(item.fechaCreacion).format('D MMM YYYY, h:mm a')}</span>
                                        </div>
                                        <div className="record-card-content">
                                            <div className="record-field">
                                                <IoPeopleOutline className="field-icon" />
                                                <span>Residente: <strong>{item.residenteNombre}</strong> (Casa {item.numeroCasa})</span>
                                            </div>
                                            <div className="record-field">
                                                <IoWarningOutline className="field-icon" />
                                                <span>Categoría: <strong>{item.categoria}</strong></span>
                                            </div>
                                            <p className="record-desc">{item.descripcion}</p>
                                            {item.evidenciaUrl && (
                                                <div className="record-evidence">
                                                    <a href={item.evidenciaUrl} target="_blank" rel="noopener noreferrer">
                                                        <IoDocumentTextOutline size={16} /> Ver evidencia cargada
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Create Sanction Modal */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>Registrar Sanción / Advertencia</h3>
                                <button className="close-btn" onClick={() => setShowModal(false)}>
                                    <IoCloseOutline size={20} />
                                </button>
                            </div>
                            <form onSubmit={handleCreateSanction} className="modal-form">
                                <div className="form-group">
                                    <label>Residente Destinatario *</label>
                                    <select
                                        value={selectedResidenteId}
                                        onChange={(e) => setSelectedResidenteId(e.target.value)}
                                        required
                                    >
                                        <option value="" disabled>Selecciona un residente...</option>
                                        {residentesList.map(r => (
                                            <option key={r.id} value={r.id}>{r.nombre} (Casa {r.numeroCasa || 'N/A'})</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Categoría de Incidencia *</label>
                                    <select
                                        value={selectedCategoria}
                                        onChange={(e) => setSelectedCategoria(e.target.value)}
                                        required
                                    >
                                        <option value="" disabled>Selecciona una categoría...</option>
                                        {categoriesList.map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>Tipo de Registro *</label>
                                    <select
                                        value={sanctionTipo}
                                        onChange={(e) => setSanctionTipo(e.target.value)}
                                        required
                                    >
                                        <option value="apercibimiento">Apercibimiento (Advertencia)</option>
                                        <option value="multa">Multa Directa</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <label style={{ margin: 0 }}>Detalles / Descripción *</label>
                                        <AIAssistantButton
                                            textoOriginal={descripcion}
                                            onSugerenciaAceptada={(nuevoTexto) => setDescripcion(nuevoTexto)}
                                            contexto="incidencias"
                                        />
                                    </div>
                                    <textarea
                                        rows="4"
                                        placeholder="Describe a detalle lo ocurrido (ej. hora del ruido, afectaciones...)"
                                        value={descripcion}
                                        onChange={(e) => setDescripcion(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>Evidencia (Foto o Video)</label>

                                    {/* Hidden file input */}
                                    <input
                                        type="file"
                                        id="evidence-file-input"
                                        accept="image/*,video/*"
                                        style={{ display: 'none' }}
                                        onChange={handleFileSelect}
                                    />

                                    {/* Action buttons to attach */}
                                    {!evidencePreview ? (
                                        <div className="evidence-attach-options" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '5px' }}>
                                            <button
                                                type="button"
                                                className="action-btn-secondary"
                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', fontSize: '0.85rem' }}
                                                onClick={() => document.getElementById('evidence-file-input').click()}
                                            >
                                                <IoImageOutline size={16} /> Galería
                                            </button>
                                            <button
                                                type="button"
                                                className="action-btn-secondary"
                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', fontSize: '0.85rem' }}
                                                onClick={() => startCamera('photo')}
                                            >
                                                <IoCameraOutline size={16} /> Usar Cámara
                                            </button>
                                            <button
                                                type="button"
                                                className="action-btn-secondary"
                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px', fontSize: '0.85rem' }}
                                                onClick={() => startCamera('video')}
                                            >
                                                <IoVideocamOutline size={16} /> Grabar Video
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="evidence-preview-container" style={{ position: 'relative', marginTop: '10px', borderRadius: '12px', overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                                            {evidenceType === 'video' ? (
                                                <video src={evidencePreview} controls style={{ width: '100%', maxHeight: '200px', background: 'black' }} />
                                            ) : (
                                                <img src={evidencePreview} alt="Evidencia cargada" style={{ width: '100%', maxHeight: '200px', objectFit: 'contain', background: '#f8fafc' }} />
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setEvidenceFile(null);
                                                    setEvidencePreview('');
                                                    setEvidenceType('');
                                                }}
                                                style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(239, 68, 68, 0.9)', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
                                            >
                                                <IoCloseOutline size={20} />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className="form-actions">
                                    <button type="button" className="btn-cancel" onClick={() => setShowModal(false)} disabled={modalLoading}>
                                        Cancelar
                                    </button>
                                    <button type="submit" className="btn-submit" disabled={modalLoading}>
                                        {modalLoading ? 'Guardando...' : 'Registrar Sanción'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Live Camera Modal Overlay */}
                {showCamera && (
                    <div className="modal-overlay" style={{ zIndex: 1100 }}>
                        <div className="modal-content" style={{ maxWidth: '450px', background: 'black', color: 'white', borderRadius: '24px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                <h3 style={{ margin: 0, color: 'white', fontSize: '1.1rem' }}>
                                    {cameraMode === 'photo' ? 'Tomar Foto de Evidencia' : 'Grabar Video de Evidencia'}
                                </h3>
                                <button onClick={stopCamera} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                    <IoCloseOutline size={26} />
                                </button>
                            </div>

                            <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                                <video
                                    id="camera-preview-video"
                                    autoPlay
                                    playsInline
                                    muted
                                    style={{ width: '100%', maxHeight: '380px', objectFit: 'cover' }}
                                />
                                {isRecording && (
                                    <div style={{ position: 'absolute', top: '15px', left: '15px', background: 'rgba(239,68,68,0.85)', padding: '5px 12px', borderRadius: '20px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                                        <IoRadioButtonOn className="blink" size={14} style={{ color: '#ef4444' }} /> Grabando...
                                    </div>
                                )}
                            </div>

                            <div style={{ padding: '24px', display: 'flex', justifyContent: 'center', gap: '15px', background: '#0f172a' }}>
                                <button
                                    type="button"
                                    className="action-btn-secondary"
                                    onClick={stopCamera}
                                    style={{ color: 'white', borderColor: 'rgba(255,255,255,0.2)', background: 'transparent' }}
                                >
                                    Cancelar
                                </button>

                                {cameraMode === 'photo' ? (
                                    <button
                                        type="button"
                                        onClick={capturePhoto}
                                        style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '12px', padding: '12px 24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: '0 4px 12px rgba(245,158,11,0.3)' }}
                                    >
                                        <IoCameraOutline size={20} /> Capturar Foto
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={isRecording ? stopRecordingVideo : startRecordingVideo}
                                        style={{ background: isRecording ? '#ef4444' : '#10b981', color: 'white', border: 'none', borderRadius: '12px', padding: '12px 24px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', boxShadow: isRecording ? '0 4px 12px rgba(239,68,68,0.3)' : '0 4px 12px rgba(16,185,129,0.3)' }}
                                    >
                                        <IoRadioButtonOn size={20} />
                                        {isRecording ? 'Detener' : 'Iniciar'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                <KondoBottomNav activeRoute="/apercibimientos" />
            </main>
        </div>
    );
}

export default ApercibimientosScreen;
